/**
 * «Темы разговоров» (#845 PR 6, спека docs/plans/2026-10-02-conversation-themes.md
 * §5) — один блок во вкладках «Продажи» (срез `outcomes`: качество и отказы) и
 * «Сайт и контент» (срез `questions`: вопросы без ответа). Работает в портале и
 * во встроенном Bitrix24 одинаково: данные идут через `useAuth`, фильтр и
 * раскрытая строка — в адресе вкладки, как у блоков вопросов.
 *
 * Бета: флаг `enableConversationThemes` по умолчанию включён у тарифов Pro и
 * выше, организация может его выключить — тогда блока нет и запрос не уходит.
 * Ответ без ключа `themes` — сервер закрыл шлюз: блока тоже нет. Набор без
 * активных тем — блока нет (#2072): один раз это говорит кнопка «Задать темы»
 * в шапке раздела, а не пустой блок на каждой вкладке. Сбой (`themesUnavailable`, ошибка запроса) — ошибка с повтором внутри
 * панели; остальная вкладка работает. Упавшее перечитывание сильнее
 * сохранённых данных: прежний ответ не выдаётся за текущий. Отказ раздела (403
 * с кодом: тариф, флаг) рисует тот, кто его хозяин: вкладка, у которой есть
 * соседний запрос к разделу, — иначе сама панель (`ownsRefusal`); у остальных
 * панель молчит, чтобы отказ не нарисовали дважды.
 *
 * Предложения новых тем запрашиваются только когда блок нарисован: закрытый
 * шлюз, сбой и отказ раздела запроса предложений не порождают.
 *
 * Круговых диаграмм нет: у разговора до трёх тем, доли не складываются в 100 %.
 */
import { useTranslation } from 'react-i18next';
import { DashboardPanel } from '@/components/dashboard/dashboard-panel';
import { useThemeProposals } from './themes/useThemeProposals';
import {
  useCanReadThemeProposals,
  useVoiceThemesAvailable,
} from './themes/useVoiceThemes';
import { VoiceLoadError } from './VoiceLoadError';
import { VoiceLocked } from './VoiceLocked';
import { tierRefusalCode } from '@/lib/tier-refusal';
import { VoiceLoading } from './VoiceLoading';
import { VoiceSectionPanel } from './VoiceSectionPanel';
import { VoiceThemeCategories } from './VoiceThemeCategories';
import { VoiceThemesHeader } from './VoiceThemesHeader';
import type { useVoiceThemeAnalytics } from './useVoiceOfCustomer';
import {
  noThemeRow,
  themeSection,
  themeSetEmpty,
  themesState,
} from './voice-themes-view-model';
import type { VoiceSearch } from './voice-search';
import { useVoiceComparable } from './voice-theme-filter-context';

export interface VoiceThemesBlockProps {
  /**
   * The tab's answer of the section (`useVoiceThemeAnalytics`): the Sales tab
   * passes the one its summary reads (#2060), so the block asks no window of
   * its own; `VoiceThemesPanel` passes its own.
   */
  query: ReturnType<typeof useVoiceThemeAnalytics>;
  search: VoiceSearch;
  onSearchChange: (patch: Partial<VoiceSearch>) => void;
  onOpenCall: (recordId: string) => void;
  /** Open the set's panel («Темы») — from the proposals line. */
  onOpenThemes?: () => void;
  /**
   * The panel is the tab's only request to the section: a refusal (403 with a
   * code) is drawn here. Without it a sibling block draws the refusal.
   */
  ownsRefusal?: boolean;
  /** Where the refusal's «upgrade» link leads. */
  upgradeHref?: string;
  /** The first rows and categories only, then «Show all N» (#2060). */
  limit?: number;
}

export function VoiceThemesBlock({
  query,
  search,
  onSearchChange,
  onOpenCall,
  onOpenThemes,
  ownsRefusal = false,
  upgradeHref,
  limit,
}: VoiceThemesBlockProps) {
  const { t } = useTranslation(['dashboard', 'common']);
  const available = useVoiceThemesAvailable();
  const canReadProposals = useCanReadThemeProposals();
  const comparable = useVoiceComparable();
  // The block is drawn: an answer that is not a failed read (the retained
  // answer of a failed refetch is not drawn), has the `themes` key and a set.
  const ready =
    available && !query.isError && query.data !== undefined
      ? themesState(query.data)
      : null;
  const drawn = ready?.kind === 'ready' && !themeSetEmpty(ready.themes);
  // The same query as the set's panel (`themeProposalKeys`): no endpoint of its
  // own, only for those who may read it, and only when the block is there.
  const proposals = useThemeProposals({ enabled: drawn && canReadProposals });

  if (!available) return null;

  const title = t('voicePage.sections.themes');
  if (query.isPending) {
    return (
      <DashboardPanel title={title}>
        <VoiceLoading />
      </DashboardPanel>
    );
  }
  if (query.isError) {
    // The section's refusal: drawn here only by the panel that owns it, else a
    // sibling block does — no second «could not load».
    const refusal = tierRefusalCode(query.error);
    if (refusal !== null) {
      return ownsRefusal ? (
        <VoiceLocked code={refusal} upgradeHref={upgradeHref} />
      ) : null;
    }
    // A failed refetch keeps the previous `data`: it is not shown as current.
    return (
      <DashboardPanel title={title}>
        <VoiceLoadError onRetry={() => void query.refetch()} />
      </DashboardPanel>
    );
  }
  const state = themesState(query.data);
  if (state.kind === 'closed') return null;
  if (state.kind === 'failed') {
    return (
      <DashboardPanel title={title}>
        <VoiceLoadError onRetry={() => void query.refetch()} />
      </DashboardPanel>
    );
  }

  const { themes } = state;
  // An empty set has no block: the header's «Set themes» says it once (§2.6).
  if (themeSetEmpty(themes)) return null;
  const section = themeSection(themes);
  const noTheme = noThemeRow(themes);
  const assigned = themes.coverage.assignedCalls;
  const onToggleRow = (rowId: string) =>
    onSearchChange({ row: search.row === rowId ? undefined : rowId });
  const proposed = proposals.data?.proposals.filter(
    (proposal) => proposal.status === 'proposed'
  );
  return (
    <VoiceSectionPanel
      section={section}
      callsInWindow={assigned}
      windowLabel={t('voicePage.themes.analytics.window', { count: assigned })}
      expandedRow={search.row}
      onToggleRow={onToggleRow}
      onOpenCall={onOpenCall}
      hint={t('voicePage.themes.analytics.hint')}
      limit={limit}
      highlightedRow={search.voiceTheme ? `theme:${search.voiceTheme}` : undefined}
      headerRows={[noTheme]}
      lowerBound={themes.truncated}
      header={(trendOf) => (
        <VoiceThemesHeader
          themes={themes}
          expandedRow={search.row}
          onToggleRow={onToggleRow}
          onOpenCall={onOpenCall}
          proposalCount={canReadProposals ? (proposed?.length ?? null) : null}
          onOpenThemes={onOpenThemes}
          noThemeTrend={trendOf(noTheme)}
        />
      )}
    >
      {assigned > 0 && <VoiceThemeCategories rows={themes.rows} limit={limit} />}
      <div className="mt-3 space-y-1 text-xs text-fg-muted">
        {themes.truncated && <p>{t('voicePage.truncated')}</p>}
        {comparable && themes.prevTruncated && <p>{t('voicePage.prevTruncated')}</p>}
        {assigned > 0 && <p>{t('voicePage.themes.analytics.denominatorNote')}</p>}
      </div>
    </VoiceSectionPanel>
  );
}
